import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { countryName } from '../lib/countries';
import type { CitySuggestion } from '../lib/types';

type Props = {
  value: string;
  countryCode: string;
  onPick: (city: string, countryCode: string) => void;
  onChangeText: (text: string) => void;
};

export default function CityAutocomplete({
  value,
  countryCode,
  onPick,
  onChangeText,
}: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CitySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Debounced fetch — 350ms is a comfortable margin above Nominatim's
  // 1 req/sec policy and feels responsive while typing.
  useEffect(() => {
    if (!open) return;
    const q = value.trim();
    if (q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await api.searchCities(q, countryCode || undefined, ctrl.signal);
        setItems(res);
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => {
      clearTimeout(handle);
    };
  }, [value, countryCode, open]);

  return (
    <div ref={wrapRef} className="combobox">
      <input
        className="input"
        placeholder={countryCode ? `City in ${countryName(countryCode)}` : 'City'}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChangeText(e.target.value);
          setOpen(true);
        }}
      />
      {open && value.trim().length >= 2 && (
        <div className="combobox-list">
          {loading ? (
            <div className="combobox-empty">Searching…</div>
          ) : items.length === 0 ? (
            <div className="combobox-empty">No matches</div>
          ) : (
            items.map((it, i) => (
              <button
                key={`${it.city}-${it.country_code}-${i}`}
                type="button"
                className="combobox-item"
                onClick={() => {
                  onPick(it.city, it.country_code);
                  setOpen(false);
                }}
              >
                <span className="city-main">{it.city}</span>
                <span className="city-sub">
                  {[it.state, it.country].filter(Boolean).join(', ')}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
